import type { Chat } from './plan';
import type { UploadedFile } from './upload';

/**
 * 파일에서 읽어 낸 값 하나.
 *
 * `confidence` 는 **칸마다 갈린다.** 이름과 날짜는 또렷한데 자격증 이름은 흐린 스캔이
 * 흔하다. 한 덩이로 「다 읽었다」고 하면 지금까지 고쳐 온 「안 넣고 넣었다고 말하는」
 * 결함을 반대 방향으로 다시 만든다.
 *
 * `evidence` 는 **문서에 실제로 적힌 문구**다. 사람이 그 자리를 찾아 확인할 수 있어야
 * 한다 — 값만 있으면 맞는지 틀린지 볼 방법이 없다.
 */
export type Fact = { label: string; value: string; confidence: number; evidence: string };

/** 이 위는 넣고, 아래는 묻는다. */
export const CONFIDENT = 0.8;

const PROMPT = `너는 사람이 올린 문서에서 값을 읽는다.

문서는 이 메시지에 함께 실려 있다. **직접 읽어라.**

규칙:
- 문서에 **실제로 적힌 것만** 낸다. 없는 항목은 아예 내지 마라 — 빈 값도 내지 마라
- 항목 이름은 문서에 적힌 말을 그대로 쓴다(성명, 입사일, 부서, 자격증번호…)
- 날짜는 YYYY-MM-DD 로 옮긴다. 그 밖의 값은 적힌 그대로 둔다
- confidence 는 **그 글자를 읽은 확신**이다. 또렷하면 0.95 위, 흐리거나 가려졌으면
  0.5 아래로 준다. 뜻을 추측해서 올리지 마라
- evidence 는 그 값이 적힌 **문서의 문구**다. 지어내지 마라

JSON 하나만 출력한다:
{"facts":[{"label":"성명","value":"홍길동","confidence":0.97,"evidence":"성명: 홍길동"}]}`;

/**
 * 올린 문서에서 값을 읽는다.
 *
 * **계획과 나눈 이유**는 둘이다. 하나는 값 하나하나에 확신을 붙이려면 그것만 하는
 * 호출이 필요하다는 것. 다른 하나는 값이다 — 계획은 검증에 걸리면 다시 도는데,
 * 그때마다 그림을 또 보내면 한 턴에 같은 그림이 세 번 나간다. 그림 한 장이 수천
 * 토큰이다.
 *
 * 읽지 못하면 빈 배열을 준다. 문서를 못 읽었다고 턴을 죽이지 않는다 — 사람이 말로
 * 시킨 것은 그대로 남아 있다.
 */
export async function extractFacts(chat: Chat, files: UploadedFile[]): Promise<Fact[]> {
  if (!files.length) return [];
  let said = '';
  try {
    said = await chat(PROMPT, { step: 'extract', files });
  } catch {
    return [];
  }
  return parseFacts(said);
}

/** 모델이 코드 울타리나 앞말을 붙여 내는 일이 있다. 첫 `{` 부터 마지막 `}` 까지 본다. */
export function parseFacts(said: string): Fact[] {
  const start = said.indexOf('{');
  const end = said.lastIndexOf('}');
  if (start < 0 || end <= start) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(said.slice(start, end + 1));
  } catch {
    return [];
  }
  const rows = (parsed as { facts?: unknown })?.facts;
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => row as Partial<Fact>)
    .map((row) => ({
      label: String(row.label ?? '').trim(),
      value: String(row.value ?? '').trim(),
      // 확신을 안 적었으면 **낮은 것으로 본다.** 높게 봐주면 흐린 값이 조용히 들어간다.
      confidence: Number.isFinite(Number(row.confidence)) ? Number(row.confidence) : 0,
      evidence: String(row.evidence ?? '').trim(),
    }))
    .filter((row) => row.label && row.value);
}

/** 넣을 것과 물을 것을 가른다. */
export function splitFacts(facts: Fact[]): { sure: Fact[]; unsure: Fact[] } {
  return {
    sure: facts.filter((one) => one.confidence >= CONFIDENT),
    unsure: facts.filter((one) => one.confidence < CONFIDENT),
  };
}

/**
 * 또렷하게 읽은 값을 계획에게 **글로** 넘긴다.
 *
 * 그림이 아니라 글이므로 계획이 몇 번을 다시 돌아도 값이 흔들리지 않는다. 그림을
 * 그대로 넘기면 판마다 다르게 읽는다 — 그것이 이 프로젝트에서 반복해 본 흔들림이다.
 */
export function describeFacts(files: UploadedFile[], sure: Fact[]): string {
  if (!sure.length) return '';
  const names = files.map((one) => one.name).join(', ');
  const lines = sure.map((one) => `- ${one.label}: ${one.value}`).join('\n');
  return `\n\n올린 문서(${names})에서 읽은 값이다. **사용자가 말한 것과 같이 다뤄라.**\n${lines}`;
}
